import { assert } from '@disy/lib/custom-error';

import type { AttributeName } from 'cadenza/model/attribute-name.gen';
import { assertValidObjectTypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';

/**
 * The ID of an attribute (active or not, see {@link ActiveAttributeId}).
 */

const ATTRIBUTE_ID_REGEX = /^(?<objecttypeId>[^/]+\/[^/]+)\/(?<attributeName>[^/]+)(\/(?<activeId>[^/]+))?$/;

/**
 * Creates an attribute ID.
 *
 * @param objecttypeId - An object type ID
 * @param attributeName - An attribute name
 * @return The created attribute ID
 */
export function createAttributeId(objecttypeId: ObjecttypeIdString, attributeName: string): AttributeId;
/**
 * Creates an active attribute ID.
 *
 * @param objecttypeId - An object type ID
 * @param attributeName - An attribute name
 * @param activeId - Identifies an active attribute
 * @return The created active attribute ID
 */
export function createAttributeId(
  objecttypeId: ObjecttypeIdString,
  attributeName: string,
  activeId: string,
): ActiveAttributeId;
export function createAttributeId(
  objecttypeId: ObjecttypeIdString,
  attributeName: string,
  activeId?: string,
): AttributeId | ActiveAttributeId {
  assertValidObjectTypeIdString(objecttypeId);
  const attributeId = [objecttypeId, attributeName, ...(activeId ? [activeId] : [])].join('/');
  assertValidAttributeId(attributeId);
  return attributeId;
}

/**
 * Parses an attribute ID into it parts.
 *
 * @param attributeId - An attribute ID
 * @return The object type ID and name of the attribute and (for an active attribute) its active ID
 */
export function parseAttributeId(
  attributeId: AttributeId | ActiveAttributeId,
): [ObjecttypeIdString, AttributeName] | [ObjecttypeIdString, AttributeName, string] {
  assertValidAttributeId(attributeId);
  // we asserted the regex to match above, so it's safe to assert non-nullability here
  const { objecttypeId, attributeName, activeId } = ATTRIBUTE_ID_REGEX.exec(attributeId)!.groups!;
  const parts = [objecttypeId, attributeName] as [ObjecttypeIdString, AttributeName];
  return activeId ? [...parts, activeId] : parts;
}

function assertValidAttributeId(value: string): asserts value is AttributeId | ActiveAttributeId {
  assert(ATTRIBUTE_ID_REGEX.test(value), `Invalid AttributeId ${value}`);
}

/**
 * Get the AttributeId part of an ActiveAttributeId.
 *
 * @param activeAttributeId
 * @return AttributeId only the attributeId part.
 */
export function getBaseAttributeId(activeAttributeId: ActiveAttributeId): AttributeId {
  const [objecttypeId, attributeName] = parseAttributeId(activeAttributeId);
  return createAttributeId(objecttypeId, attributeName);
}

export function isPartOfObjecttype(attributeId: AttributeId, objecttypeId: ObjecttypeIdString) {
  return attributeId.startsWith(objecttypeId);
}
